import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, History, CheckCircle2, Clock, FileText, Trophy, ShieldCheck, AlertTriangle, XCircle, Code2, Download, Lock } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { STATUS_LABELS } from "@/lib/i18n";
import { limitsFor } from "@/lib/plan";
import LockedCard from "@/components/LockedCard";

export default function Reports() {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang, t } = useLang();
  const limits = limitsFor(user);
  const fullReport = !!limits.full_report;
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!fullReport) {
      setLoading(false);
      return;
    }
    api.get(`/reports/${id}`, { params: { lang } }).then((r) => setReport(r.data)).finally(() => setLoading(false));
  }, [id, fullReport, lang]);

  // Streams the PDF response as a Blob (axios default JSON parse won't handle bytes).
  // Saves via a temporary <a download> — works on Chrome/Safari/Firefox desktop + mobile.
  const downloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await api.get(`/reports/${id}/pdf`, {
        responseType: "blob",
        params: { lang },
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const fname = m?.[1] || "uSeen-AI-Gorunurluk-Raporu.pdf";
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(t("reports.downloadSuccess"));
    } catch (err) {
      const detail = err?.response?.data;
      let msg = t("reports.downloadError");
      if (typeof detail === "string") msg = detail;
      else if (detail?.detail) msg = detail.detail;
      else if (detail instanceof Blob) {
        try { msg = JSON.parse(await detail.text())?.detail || msg; } catch (_) {}
      }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  if (!fullReport) {
    return (
      <div className="space-y-6" data-testid="reports-page">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">
              <FileText size={12} /> {t("reports.eyebrow")}
            </div>
            <h1 className="font-display text-4xl font-bold text-white">{t("reports.title")}</h1>
          </div>
          {/* Locked download button — visible but disabled for free/starter users. */}
          <div className="flex flex-col items-start sm:items-end gap-1.5" data-testid="pdf-locked-block">
            <button
              type="button"
              disabled
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium border border-white/10 bg-white/[0.03] text-slate-500 cursor-not-allowed"
              data-testid="pdf-download-locked"
            >
              <Lock size={14} /> {t("reports.lockedPdf")}
            </button>
            <div className="text-[11px] text-slate-500 max-w-[220px] text-left sm:text-right">
              {t("reports.lockedPdfHint")}
            </div>
            <Link
              to="/app/planlar"
              className="text-[11px] text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
              data-testid="pdf-locked-cta-plans"
            >
              {t("reports.lockedPdfCta")}
            </Link>
          </div>
        </div>
        <LockedCard
          title={t("reports.lockTitle")}
          description={t("reports.lockDesc")}
          requiredPlan="Growth"
          testid="reports-locked"
        />
      </div>
    );
  }

  if (loading) return <div className="text-slate-400 flex items-center"><Loader2 className="animate-spin mr-2" /> {t("common.loading")}</div>;
  if (!report?.latest_scan) return <div className="text-slate-400">{t("comp.runAnalysisFirst")}</div>;

  const { business, latest_scan, previous_scan, scan_history, tasks, contents, rescans } = report;
  // Match both Turkish canonical and English-translated status (backend
  // returns the translated form when lang=en).
  const isCompleted = (s) => s === "Tamamlandı" || s === "Completed";
  const completed = tasks.filter((task) => isCompleted(task.status));
  const open = tasks.filter((task) => !isCompleted(task.status));
  const next_steps = latest_scan.next_steps || [];
  const tech = latest_scan.technical_signals || {};
  const robots = tech.robots || {};
  const fetched = latest_scan.fetched || {};
  const techTasks = tasks.filter((t) =>
    ["Yapılandırılmış Veri", "FAQ Schema", "Robots.txt"].includes(t.category)
  );

  const techRows = [
    {
      key: "robots",
      label: t("reports.tech.robots"),
      ok: robots.site_open !== false && !robots.important_paths_blocked && (robots.ai_bots_blocked || []).length === 0,
      warn: robots.found === false,
      hint: robots.status_message || "—",
    },
    {
      key: "schema",
      label: t("reports.tech.schema"),
      ok: !!tech.has_schema,
      hint: tech.has_schema
        ? t("reports.tech.schemaFound", { types: (tech.schema_types || []).join(", ") || t("score.signal.schema.typesUnknown") })
        : t("reports.tech.schemaMissing"),
    },
    {
      key: "faq_schema",
      label: t("reports.tech.faqSchema"),
      ok: !!tech.has_faq_schema,
      hint: tech.has_faq_schema ? t("reports.tech.faqSchemaFound") : t("reports.tech.faqSchemaMissing"),
    },
    {
      key: "local_schema",
      label: t("reports.tech.localSchema"),
      ok: !!tech.has_local_business_schema,
      hint: tech.has_local_business_schema ? t("reports.tech.localFound") : t("reports.tech.localMissing"),
    },
  ];

  const dateLocale = lang === "en" ? "en-US" : "tr-TR";
  const chartData = [...scan_history].reverse().map((s, i) => ({
    name: `T${i + 1}`,
    skor: s.overall_score,
    date: new Date(s.created_at).toLocaleDateString(dateLocale),
  }));

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">{t("reports.eyebrow")}</div>
          <h1 className="font-display text-4xl font-bold text-white">{business.name}</h1>
          <p className="text-slate-400 mt-1">{business.sector} · {business.city}</p>
        </div>
        <button
          type="button"
          onClick={downloadPdf}
          disabled={downloading}
          className="neon-btn inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          data-testid="pdf-download-btn"
        >
          {downloading ? (
            <><Loader2 size={14} className="animate-spin" /> {t("reports.downloadingPdf")}</>
          ) : (
            <><Download size={14} /> {t("reports.downloadPdf")}</>
          )}
        </button>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Kpi label={t("reports.kpi.current")} value={latest_scan.overall_score} accent="text-cyan-300" />
        <Kpi label={t("reports.kpi.previous")} value={previous_scan?.overall_score ?? "-"} accent="text-slate-300" />
        <Kpi label={t("reports.kpi.completed")} value={completed.length} accent="text-emerald-300" />
        <Kpi label={t("reports.kpi.open")} value={open.length} accent="text-amber-300" />
      </div>

      {chartData.length >= 2 ? (
        <div className="glass-strong rounded-3xl p-6">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-4">{t("reports.timelineTitle")}</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <defs>
                  <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#22d3ee" />
                    <stop offset="100%" stopColor="#a855f7" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="name" stroke="rgba(148,163,184,0.6)" fontSize={11} />
                <YAxis stroke="rgba(148,163,184,0.6)" fontSize={11} domain={[0, 100]} />
                <Tooltip
                  contentStyle={{ background: "rgba(10,10,18,0.95)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }}
                  labelStyle={{ color: "#94a3b8" }}
                />
                <Line type="monotone" dataKey="skor" stroke="url(#lineGrad)" strokeWidth={3} dot={{ fill: "#3b82f6", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div
          className="glass-strong rounded-3xl p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          data-testid="timeline-empty-state"
        >
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500/15 to-purple-500/15 border border-white/10 flex items-center justify-center shrink-0">
            <History size={20} className="text-cyan-300" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">{t("reports.timelineTitle")}</div>
            <h3 className="font-display text-lg font-semibold text-white mb-1">
              {t("reports.timelineEmpty.title")}
            </h3>
            <p className="text-sm text-slate-400 max-w-xl leading-relaxed">
              {t("reports.timelineEmpty.desc")}
            </p>
          </div>
        </div>
      )}

      <div className="glass-strong rounded-3xl p-6" data-testid="technical-signals-panel">
        <div className="flex items-center gap-2 mb-4">
          <Code2 size={16} className="text-cyan-300" />
          <h3 className="font-display text-xl font-semibold text-white">{t("reports.techPanel")}</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {techRows.map((r) => {
            const Icon = r.ok ? CheckCircle2 : (r.warn ? AlertTriangle : XCircle);
            const accent = r.ok
              ? "text-emerald-400 bg-emerald-500/5 border-emerald-500/15"
              : r.warn
              ? "text-amber-300 bg-amber-500/5 border-amber-500/20"
              : "text-red-300 bg-red-500/5 border-red-500/15";
            return (
              <div
                key={r.key}
                className={`rounded-xl border p-3 flex gap-3 items-start ${accent}`}
                data-testid={`tech-row-${r.key}`}
              >
                <Icon size={16} className="mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white">{r.label}</div>
                  <div className="text-xs text-slate-300 mt-1 leading-relaxed">{r.hint}</div>
                </div>
              </div>
            );
          })}
        </div>
        {techTasks.length > 0 && (
          <div className="mt-5 pt-4 border-t border-white/5">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">{t("reports.tech.openTasks")}</div>
            <ul className="space-y-1.5">
              {techTasks.map((task) => (
                <li key={task.id} className="text-sm text-slate-300 flex items-center gap-2">
                  <ShieldCheck size={13} className={isCompleted(task.status) ? "text-emerald-400" : "text-amber-300"} />
                  <span>{task.title}</span>
                  <span className="text-[11px] text-slate-500 ml-auto">{STATUS_LABELS[lang]?.[task.status] || task.status}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="glass-strong rounded-3xl p-6">
        <h3 className="font-display text-xl font-semibold text-white mb-4">{t("reports.explanation")}</h3>
        <p className="text-slate-300 text-sm leading-relaxed">{latest_scan.score_explanation}</p>
      </div>

      {(latest_scan.weaknesses || []).length > 0 && (
        <div className="glass-strong rounded-3xl p-6 border border-red-500/15">
          <h3 className="font-display text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse-glow" /> {t("reports.criticalGaps")}
          </h3>
          <ul className="space-y-2">
            {(latest_scan.weaknesses || []).map((s, i) => (
              <li key={i} className="flex gap-3 items-start rounded-lg bg-red-500/5 border border-red-500/10 p-3 text-sm text-slate-200">
                <span className="w-6 h-6 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center text-[10px] font-mono text-red-300 shrink-0">{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {next_steps.length > 0 && (
        <div className="glass-strong rounded-3xl p-6">
          <h3 className="font-display text-xl font-semibold text-white mb-4">{t("reports.nextSteps")}</h3>
          <ol className="space-y-2">
            {next_steps.map((s, i) => (
              <li key={i} className="flex gap-3 items-start text-sm text-slate-200">
                <span className="w-6 h-6 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-xs font-mono text-cyan-300 shrink-0">{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <Section title={t("reports.section.completed")} icon={CheckCircle2} accent="text-emerald-400">
          {completed.length === 0 ? <Empty>{t("reports.section.empty.completed")}</Empty> : (
            <ul className="space-y-2">
              {completed.map((task) => (
                <li key={task.id} className="text-sm text-slate-300 flex items-center justify-between gap-2 rounded-lg bg-white/[0.02] p-3">
                  <span>{task.title}</span>
                  <span className="text-xs text-emerald-300 font-mono">+{task.impact_points}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={t("reports.section.open")} icon={Clock} accent="text-amber-400">
          {open.length === 0 ? <Empty>{t("reports.section.empty.open")}</Empty> : (
            <ul className="space-y-2">
              {open.map((task) => (
                <li key={task.id} className="text-sm text-slate-300 flex items-center justify-between gap-2 rounded-lg bg-white/[0.02] p-3">
                  <span>{task.title}</span>
                  <span className="text-xs text-amber-300">{STATUS_LABELS[lang]?.[task.status] || task.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <Section title={t("reports.section.contents")} icon={FileText} accent="text-cyan-400">
        {contents.length === 0 ? <Empty>{t("reports.section.empty.contents")}</Empty> : (
          <ul className="space-y-2">
            {contents.map((c) => (
              <li key={c.id} className="text-sm text-slate-300 rounded-lg bg-white/[0.02] p-3 flex items-center justify-between">
                <span>{c.content_type}</span>
                <span className="text-xs font-mono text-slate-500">{new Date(c.created_at).toLocaleDateString(dateLocale)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={t("reports.section.rescans")} icon={History} accent="text-purple-400">
        {rescans.length === 0 ? <Empty>{t("reports.section.empty.rescans")}</Empty> : (
          <ul className="space-y-2">
            {rescans.map((r) => (
              <li key={r.id} className="text-sm text-slate-300 rounded-lg bg-white/[0.02] p-3">
                <div className="flex items-center justify-between">
                  <span className="text-white">{STATUS_LABELS[lang]?.[r.verdict?.new_status] || r.verdict?.new_status || t("reports.scan")}</span>
                  <span className="text-xs font-mono text-slate-500">{new Date(r.created_at).toLocaleDateString(dateLocale)}</span>
                </div>
                <div className="text-xs text-slate-400 mt-1">{r.verdict?.evidence}</div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="glass rounded-2xl p-5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{label}</div>
      <div className={`font-mono text-3xl ${accent}`}>{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, accent, children }) {
  return (
    <div className="glass rounded-3xl p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className={accent} />
        <h3 className="font-display font-semibold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-sm text-slate-500 py-2">{children}</div>;
}
